import "./login.css";
import { Button, Form } from "react-bootstrap";
import { Logo } from "../logo/logo";
import { ArrowLeft } from "../icons";
import { Container, ErrorText, InnerContainer } from "../commons";

import { useSignIn } from "react-auth-kit";
import { useFormik } from "formik";
import axios, { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { createT } from "../../i18n/strings";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

const Login = (props: any) => {
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const signIn = useSignIn();
  const navigate = useNavigate();
  // Auth pages are intentionally Swedish-only (see createT) so signup never
  // flips between languages mid-flow regardless of the stored UI language.
  const t = useMemo(() => createT("sv"), []);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Where to go after login: the page RequireAuth bounced from, or ?next= (the
  // marketing site's "log in to reply" links). Same-origin paths only.
  const fromState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const requested = searchParams.get("next") || (fromState?.pathname ? `${fromState.pathname}${fromState.search ?? ""}${fromState.hash ?? ""}` : "");
  const afterLogin = /^\/(?![/\\])/.test(requested) && !requested.startsWith("/login") ? requested : "/";

  // Check for redirect reason (e.g., permissions changed)
  useEffect(() => {
    const reason = searchParams.get("reason");
    if (reason === "permissions_changed") {
      setInfo(t("login.permissionsChanged"));
    }
  }, [searchParams, t]);

  // Force light theme on login page only
  useEffect(() => {
    const prev = document.documentElement.getAttribute("data-bs-theme");
    document.documentElement.setAttribute("data-bs-theme", "light");
    return () => {
      if (prev) document.documentElement.setAttribute("data-bs-theme", prev);
    };
  }, []);

  const onSubmit = async (values: any) => {
    setError("");

    try {
      const response = await axios.post(`${apiUrl}/api/auth/login`, values);

      signIn({
        token: response.data.accessToken,
        // Minutes. Matches the 7d access token; refreshToken (30d) is required
        // now that AuthProvider enables the refresh-token feature.
        expiresIn: 60 * 24 * 7,
        tokenType: "Bearer",
        refreshToken: response.data.refreshToken,
        refreshTokenExpireIn: 60 * 24 * 30,
        authState: {
          id: response.data.account.id,
          email: response.data.account.email,
          username: response.data.account.username,
          image_link: response.data.account.image_link,
          roles: response.data.account.roles || [],
          permissions: response.data.account.permissions || [],
          permission_version: response.data.account.permission_version,
        },
      });
      navigate(afterLogin);
    } catch (err) {
      if (err && err instanceof AxiosError) {
        const errorCode = err.response?.data.error;
        if (errorCode === "EmailNotVerified") {
          setEmailNotVerified(true);
          setUnverifiedEmail(err.response?.data.email || values.email);
          setError(t("login.emailNotVerified"));
        } else if (errorCode === "Incorrect email or password.") {
          setError(t("login.incorrectCredentials"));
        } else {
          setError(err.response?.data.message || err.response?.data.error);
        }
      } else if (err && err instanceof Error) {
        setError(err.message);
      }
      console.log("Error: ", err);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${apiUrl}/api/verify/resend`, {
        email: unverifiedEmail,
      });
      setResendSuccess(true);
      setError("");
    } catch (err) {
      console.log("Resend error:", err);
    } finally {
      setResending(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit,
  });

  return (
    <Container className="login-container">
      <span className="auth-blob b1" aria-hidden="true" />
      <span className="auth-blob b2" aria-hidden="true" />
      <span className="auth-blob b3" aria-hidden="true" />
      <a href="https://spotprisprognos.se" className="auth-back">
        <ArrowLeft style={{ width: "0.9em", height: "0.9em", verticalAlign: "-0.12em" }} /> Tillbaka
      </a>
      <div className="banner-text">
        <h1>{t("login.banner")}</h1>
      </div>
      <InnerContainer className="login-inner-container">
        <div className="logo-section">
          <Logo size="medium" showCredit />
        </div>
        <Form onSubmit={formik.handleSubmit}>
          <h1>{t("login.welcome")}</h1>
          {info && (
            <div className="alert alert-info mb-3" role="alert">
              {info}
            </div>
          )}
          <ErrorText>{error}</ErrorText>

          {emailNotVerified && !resendSuccess && (
            <div className="mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleResendVerification}
                disabled={resending}
                style={{ width: "100%" }}
              >
                {resending
                  ? t("emailVerification.sending")
                  : t("login.resendVerification")}
              </Button>
            </div>
          )}

          {resendSuccess && (
            <div className="alert alert-success mb-3" role="alert">
              {t("login.verificationResent")}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Control
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={t("login.email")}
              type="email"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              placeholder={t("login.password")}
              type="password"
            />
          </Form.Group>

          <div className="mt-4">
            <Button
              variant="primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {t("login.button")}
            </Button>
          </div>

          <div className="register-link">
            {t("login.noAccount")}
            <a href="/register">{t("login.registerLink")}</a>
          </div>
        </Form>
      </InnerContainer>
    </Container>
  );
};

export { Login };
